/**
 * Company: SPHMedia
 * Description: Filter for keyword section page
 */

import { ReactElement } from "react";
import { createPortal } from "react-dom";
import { KeywordButtonFactory } from "@components/Button/utils/helpers";
import { KeywordButtonType } from "@components/Button/utils/types";
import FollowKeywordModal from "@components/FollowKeyword/FollowKeywordModal";
import { useHandleMyBtKeywords } from "@features/mybt/hooks/useHandleMyBtKeywords";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { FeedTag } from "@routes/Keyword/utils/types";
import { cn } from "@util/helpers";

import KeywordFilterTooltip from "./components/KeywordFilterTooltip";
import { KeywordLimitModalFactory } from "./utils/helpers";
import { KeywordType } from "./utils/types";
import { KeywordFilterFactory } from "./KeywordTypeFactory";

export type KeywordFilterProps = {
  /** Test id for the component */
  testId?: string;
  /** Classname for the root element */
  className?: string;
  /** Classname for the tag element */
  tagClassName?: string;
  /** Classname for the icon element */
  iconClassName?: string;
  /** Keyword tag object  */
  tag: FeedTag;
  /** A boolean indicating whether the keyword is followed. */
  isFollowed?: boolean;
  /** A boolean indicating whether should tooltip disable. */
  disableTooltip?: boolean;
  /** A boolean indicating whether should border display. */
  disableBorder?: boolean;
  /** A boolean indicating whether tag is render with link. */
  isLinkenabled?: boolean;
  /** Classname for the tooltip arrow element */
  toolTipArrowClassName?: string;
  /** Classname for the tooltip element */
  toolTipClassName?: string;
  /** A boolean indicating whether is component added in keyword listing page. */
  isKeywordListing?: boolean;
  /** Type of the tag text */
  keywordType?: KeywordType;
  /** Type of the button */
  keywordButtonType?: KeywordButtonType;
  /** Classname for the button element  */
  buttonClassName?: string;
  /** extra function to be called on click */
  extraFunction?: () => void;
  /** Ref code to be added on the tag links. */
  refCode?: string;
};

/**
 * KeywordFilter
 * Description: Display the keyword filter in the vertical section page
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
const KeywordFilter = ({
  testId,
  tag,
  className,
  tagClassName,
  disableTooltip = false,
  disableBorder = false,
  isLinkenabled = true,
  toolTipArrowClassName,
  toolTipClassName,
  keywordType,
  keywordButtonType = KeywordButtonType.default,
  buttonClassName,
  iconClassName,
  refCode,
}: KeywordFilterProps): ReactElement => {
  const { isAnonymous } = useUserInfo();

  const {
    isTooltipOpen,
    setIsTooltipOpen,
    isModalShown,
    errorMessages,
    method,
    isLoading,
    isFollowed,
    handleFollow,
    handleUnfollow,
    handleLimitModalClose,
    isLimitModalShown,
  } = useHandleMyBtKeywords({
    tag,
    keywordButtonType,
  });

  const renderWithTag =
    isLinkenabled && tag.urlPath ? KeywordType.withTag : KeywordType.default;

  const keywordProps = {
    tag,
    tagDisplayName: tag.name,
    disableBorder,
    tagClassName,
    keywordType: keywordType ? keywordType : renderWithTag,
    refCode,
  };

  const KeywordButton = KeywordButtonFactory({
    keywordButtonType,
  });

  const KeywordLimitModal = KeywordLimitModalFactory({
    keywordButtonType,
  });

  return (
    <div
      data-testid={testId}
      className={cn("relative text-black", className)}
      onMouseEnter={() => {
        setIsTooltipOpen(true);
      }}
      onMouseLeave={() => {
        setIsTooltipOpen(false);
      }}
    >
      <div className="group relative">
        <div className="flex items-center">
          <KeywordFilterFactory
            {...keywordProps}
            handleClick={isFollowed ? handleUnfollow : handleFollow}
          />

          <KeywordButton
            handleClick={isFollowed ? handleUnfollow : handleFollow}
            isLoading={isLoading}
            isTagFollowed={isFollowed}
            keyword={tag}
            className={buttonClassName}
            iconClassName={iconClassName}
            isAnonymousUser={isAnonymous}
          />
        </div>

        {isTooltipOpen && !disableTooltip ? (
          <KeywordFilterTooltip
            toolTipClassName={toolTipClassName}
            toolTipArrowClassName={toolTipArrowClassName}
          />
        ) : null}
      </div>

      {isModalShown &&
      (isFollowed || errorMessages) &&
      keywordButtonType !== KeywordButtonType.version3
        ? createPortal(
            <FollowKeywordModal hasError={errorMessages} type={method} />,
            document.body
          )
        : null}

      <KeywordLimitModal
        onClose={() => {
          handleLimitModalClose(false);
        }}
        isModalOpen={isLimitModalShown}
      />
    </div>
  );
};

export default KeywordFilter;
